import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ApiError,
  fetchAgents,
  fetchUserPermissions,
  fetchUsers,
  grantPermission,
  revokePermission,
  type AdminUser,
} from '../../lib/api';
import type { UserPermission } from '../../lib/auth';

const GRANULAR_ROLES = new Set(['operator', 'viewer']);

const PERMISSION_COLOR: Record<string, { color: string; bg: string }> = {
  read: { color: '#d1d5db', bg: '#374151' },
  command: { color: '#93c5fd', bg: '#1c4271' },
  admin: { color: '#ffaaaa', bg: '#6e1f1f' },
};

export function PermissionsPage(): JSX.Element {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [permission, setPermission] = useState<'read' | 'command' | 'admin'>('read');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const agentsQuery = useQuery({ queryKey: ['agents'], queryFn: fetchAgents });

  const granularUsers: AdminUser[] = (usersQuery.data ?? []).filter(
    (u) => GRANULAR_ROLES.has(u.role) && u.isActive
  );

  const permsQuery = useQuery({
    queryKey: ['userPermissions', selectedUserId],
    queryFn: () => fetchUserPermissions(selectedUserId),
    enabled: selectedUserId !== '',
  });

  const queryClient = useQueryClient();

  const grantMutation = useMutation({
    mutationFn: () =>
      grantPermission(selectedUserId, { agentId: selectedAgentId, permission }),
    onSuccess: () => {
      setFeedback({ kind: 'success', text: 'Permission granted successfully.' });
      void queryClient.invalidateQueries({ queryKey: ['userPermissions', selectedUserId] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.messages[0]?.message : null;
      setFeedback({ kind: 'error', text: msg ?? 'Failed to grant permission.' });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (permId: number) => revokePermission(selectedUserId, permId),
    onSuccess: () => {
      setFeedback({ kind: 'success', text: 'Permission revoked.' });
      void queryClient.invalidateQueries({ queryKey: ['userPermissions', selectedUserId] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.messages[0]?.message : null;
      setFeedback({ kind: 'error', text: msg ?? 'Failed to revoke permission.' });
    },
  });

  function handleGrant(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFeedback(null);
    if (selectedUserId === '' || selectedAgentId === '') {
      setFeedback({ kind: 'error', text: 'Select a user and an agent.' });
      return;
    }
    grantMutation.mutate();
  }

  function handleRevoke(perm: UserPermission): void {
    if (!window.confirm(`Revoke permission '${perm.permission}' on ${perm.agentHostname}?`)) {
      return;
    }
    setFeedback(null);
    revokeMutation.mutate(perm.id);
  }

  if (usersQuery.isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading users…
        </Typography>
      </Box>
    );
  }
  if (usersQuery.isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Error: {usersQuery.error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        Permissions administration
      </Typography>

      <Stack spacing={3}>
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel>User</InputLabel>
          <Select
            value={selectedUserId}
            label="User"
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setFeedback(null);
            }}
          >
            <MenuItem value="">— choose —</MenuItem>
            {granularUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.username} ({u.role}) — {u.fullName ?? '—'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {feedback !== null ? (
          <Alert severity={feedback.kind === 'success' ? 'success' : 'error'}>
            {feedback.text}
          </Alert>
        ) : null}

        {selectedUserId === '' ? (
          <Typography variant="body2" color="text.secondary">
            Select a user to view or edit permissions.
          </Typography>
        ) : (
          <>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current permissions
              </Typography>
              {permsQuery.isLoading ? (
                <Typography variant="body2">Loading…</Typography>
              ) : (permsQuery.data ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No granular access. Use the form below to grant one.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell>Permission</TableCell>
                      <TableCell>Granted at</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(permsQuery.data ?? []).map((p) => {
                      const cfg = PERMISSION_COLOR[p.permission] ?? PERMISSION_COLOR.read!;
                      return (
                        <TableRow key={p.id}>
                          <TableCell>{p.agentHostname}</TableCell>
                          <TableCell>
                            <Chip
                              label={p.permission}
                              size="small"
                              sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>{p.grantedAt}</TableCell>
                          <TableCell>{p.grantedBy}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleRevoke(p)}
                              disabled={revokeMutation.isPending}
                            >
                              Revoke
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Grant new permission
              </Typography>
              <Box component="form" onSubmit={handleGrant}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel>Agent</InputLabel>
                    <Select
                      value={selectedAgentId}
                      label="Agent"
                      onChange={(e) => setSelectedAgentId(e.target.value)}
                      disabled={grantMutation.isPending || agentsQuery.isLoading}
                    >
                      <MenuItem value="">— choose —</MenuItem>
                      {(agentsQuery.data ?? []).map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.hostname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Permission</InputLabel>
                    <Select
                      value={permission}
                      label="Permission"
                      onChange={(e) =>
                        setPermission(e.target.value as 'read' | 'command' | 'admin')
                      }
                      disabled={grantMutation.isPending}
                    >
                      <MenuItem value="read">read (view only)</MenuItem>
                      <MenuItem value="command">command (issue commands)</MenuItem>
                      <MenuItem value="admin">admin (full)</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={grantMutation.isPending || selectedAgentId === ''}
                  >
                    {grantMutation.isPending ? 'Granting…' : 'Grant'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </>
        )}
      </Stack>
    </Box>
  );
}
